/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// UI lib components
import { Group, SegmentedControl, Stack, ThemeIcon } from '@mantine/core';

// UI local components
import ResourceTable from '@features/Resource/ResourceTable';
import SurfacePanel from '@ui-kit/organisms/SurfacePanel';

// Store
import useWorkspace from '@features/Workspace/store/hooks/useWorkspace';

// API & types
import {
  ListResourcesQueryVariables,
  ResourceIndexView,
  ResourceRowFragmentFragment,
} from '@app-types/blackroom-api';
import { ResourcePaneProps, ResourceNavigationView } from './types';

import { vars } from '@app/shared/themes/vars';
import ActionBar from './ActionBar';
import { useTranslation } from 'react-i18next';
import { palette } from '@app/shared/ui-kit/colors';
import { IconFolderOpen, IconListNumbers } from '@tabler/icons-react';
import { Text } from '@mantine/core';
import { FONT_WEIGHT } from '@app/shared/ui-kit/fonts';
import {
  MRT_RowSelectionState,
  MRT_RowVirtualizer,
  MRT_SortingState,
  MRT_TableInstance,
} from 'mantine-react-table';
import ResourceList from '../ResourceList';
import { useParams } from 'react-router-dom';
import { getResourceColumnSchema, PAGE_SIZE } from '../ResourceTable/types';
import ResourceFoldersIndexView from '../ResourceFoldersIndexView';
import { getResourceFilterFromScope } from '@helpers/api';
import { convertSortingToResourceSorter } from '../ResourceTable/utils';
import useAccount from '@app/features/Account/store/useAccount';
import * as classes from './styles.css';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */

function ResourcePane({
  activeResource,
  parent,
  scope = 'my',
  onClickResource,
  ...props
}: ResourcePaneProps) {
  /* ********************************** HOOKS ********************************* */
  const columns = getResourceColumnSchema(scope);

  const INITIAL_RESOURCE_TABLE_ROW_SELECTION = {};

  // State

  const [selectedResources, setSelectedResources] = useState<
    ResourceRowFragmentFragment[]
  >([]);
  const [navigationView, setNavigationView] =
    useState<ResourceNavigationView>('folder');
  const [resourceTableColumnVisibility, setResourceTableColumnVisibility] =
    useState(columns);
  const [resourceTableRowSelection, setResourceTableRowSelection] =
    useState<MRT_RowSelectionState>(INITIAL_RESOURCE_TABLE_ROW_SELECTION);
  const [table, setTable] =
    useState<MRT_TableInstance<ResourceRowFragmentFragment> | null>(null);
  const [selectAll, setSelectAll] = useState<boolean>(false);
  const [highlightedRowId, setHighlightedRowId] = useState<string | null>(null);
  const [sorting, setSorting] = useState<MRT_SortingState>([]);

  const highlightTimeoutRef = useRef<NodeJS.Timeout>();
  const fullIndexTableVirtualizerInstanceRef = useRef<MRT_RowVirtualizer>(null);
  const fullIndexTableDataRef = useRef<ResourceIndexView[]>([]);
  const resourceTableVirtualizerInstanceRef = useRef<MRT_RowVirtualizer>(null);

  // Store
  const workspace = useWorkspace();
  const { isCurrentWorkspaceAdmin, hasPermission } = useAccount();
  const canReorderResources =
    isCurrentWorkspaceAdmin || hasPermission('manageResources');

  // Routing
  const { folderId: parentId } = useParams();

  // Localization
  const { t } = useTranslation(['Resource']);

  // Side-effects
  useEffect(() => {
    if (activeResource && onClickResource) {
      onClickResource(undefined);
    }
    setResourceTableRowSelection(INITIAL_RESOURCE_TABLE_ROW_SELECTION);
    setSelectedResources([]);
    setResourceTableColumnVisibility(columns);
  }, [navigationView]);

  useEffect(() => {
    setResourceTableColumnVisibility(columns);
  }, [scope]);

  // Reset to default sorting
  useEffect(() => {
    const newSorting: MRT_SortingState = [];

    if (scope === 'archived') {
      newSorting.push({ id: 'archivedAt', desc: true });
    }

    setSorting(newSorting);
  }, [scope]);

  // Makes sure to reset activeResource in case of a reset of row selection
  useEffect(() => {
    if (Object.keys(resourceTableRowSelection).length === 0) {
      onClickResource?.(undefined);
    }
  }, [resourceTableRowSelection]);

  useEffect(() => {
    if (parentId) {
      setNavigationView('folder');
    }
  }, [parentId]);

  const handleFolderClick = useCallback(
    (folder: ResourceIndexView) => {
      // Find the row in the table that matches this folder
      const rowIndex = fullIndexTableDataRef.current.findIndex(
        (r) => r.id === folder.id,
      );

      // Scroll to element
      if (rowIndex !== -1) {
        fullIndexTableVirtualizerInstanceRef.current?.scrollToIndex(rowIndex, {
          align: 'center',
        });

        // Set the highlighted row
        setHighlightedRowId(folder.id);
        // Clear the highlight after 1.5 seconds
        highlightTimeoutRef.current = setTimeout(() => {
          setHighlightedRowId(null);
        }, 1500);
      }
    },
    [fullIndexTableVirtualizerInstanceRef, fullIndexTableDataRef],
  );

  useEffect(() => {
    return () => {
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }
    };
  }, []);

  /* ***************************** LOCAL VARIABLES **************************** */

  const workspaceId = workspace.id;

  // Construct fetchVariables for queries
  let resolvedParentId: string | null | undefined;
  if (navigationView === 'folder') {
    resolvedParentId = parentId ?? null;
  }
  if (scope === 'archived' || scope == 'search' || scope === 'recent') {
    resolvedParentId = undefined;
  }

  // Construct requestVariables for refetch operations
  const requestVariables: ListResourcesQueryVariables = useMemo(
    () => ({
      filter: {
        workspaceId,
        parentId: resolvedParentId,
        ...getResourceFilterFromScope(scope),
      },
      sorter: convertSortingToResourceSorter(sorting),
      limit: PAGE_SIZE,
      offset: 0,
    }),
    [scope, workspaceId, resolvedParentId, sorting],
  );

  /* ***************************** RENDER HELPERS ***************************** */

  function getIndexedFolders() {
    if (navigationView === 'folder' || scope !== 'my') {
      return null;
    }
    return (
      <SurfacePanel w="29%" h="100%" className={classes.tableSurface}>
        <ResourceFoldersIndexView onFolderClick={handleFolderClick} />
      </SurfacePanel>
    );
  }

  function getResources() {
    if (navigationView === 'folder' || scope !== 'my') {
      return (
        <SurfacePanel w="100%" h="100%" className={classes.tableSurface}>
          <ResourceTable
            key={`resource-table-${navigationView}`}
            scope={scope}
            state={{
              columnVisibility: resourceTableColumnVisibility,
              rowSelection: resourceTableRowSelection,
            }}
            onTableReady={setTable}
            rowVirtualizerInstanceRef={resourceTableVirtualizerInstanceRef}
            onClickResource={onClickResource}
            parent={parent}
            selectedResources={selectedResources}
            mantineTableBodyCellProps={({ row, cell }) => ({
              onClick: () => {
                if (
                  onClickResource &&
                  navigationView === 'folder' &&
                  cell.column.id !== 'actions' &&
                  cell.column.id !== 'favorite'
                ) {
                  onClickResource(row.original);
                }
              },
            })}
            onSelectResources={setSelectedResources}
            onSelectAllResources={setSelectAll}
            selectAll={selectAll}
            onRowSelectionChange={setResourceTableRowSelection}
            onColumnVisibilityChange={setResourceTableColumnVisibility}
            enableRowOrdering={
              navigationView === 'folder' &&
              scope === 'my' &&
              canReorderResources
            }
            enableExpanding={false}
            enableRowSelection
            withNavigation
            requestVariables={requestVariables}
            sorting={sorting}
            onSortingChange={setSorting}
          />
        </SurfacePanel>
      );
    }
    return (
      <SurfacePanel w="70%" h="100%" className={classes.tableSurface}>
        <ResourceList
          key={`resource-table-${navigationView}`}
          rowVirtualizerInstanceRef={fullIndexTableVirtualizerInstanceRef}
          scope={scope}
          initialState={{
            sorting: [{ id: 'index', desc: false }],
            expanded: true,
            columnVisibility: { 'mrt-row-expand': false },
          }}
          fetchVariables={{
            filter: {
              workspaceId,
            },
          }}
          onDataChange={(data) => {
            fullIndexTableDataRef.current = data;
          }}
          mantineTableBodyRowProps={({ row }) => ({
            style: {
              height: 60,
              backgroundColor:
                row.original.id === highlightedRowId
                  ? vars.colors.blackroomBlue[1]
                  : 'transparent',
              '--mrt-row-hover-background-color':
                row.original.id === highlightedRowId
                  ? vars.colors.blackroomBlue[1]
                  : 'transparent',
              '--mrt-striped-row-hover-background-color':
                row.original.id === highlightedRowId
                  ? vars.colors.blackroomBlue[1]
                  : 'transparent',
              transition: 'background-color 0.2s ease-in-out',
            },
          })}
          enableRowOrdering={false}
          enableExpanding={true}
          getRowCanExpand={(row) => row.subRows.length > 0}
          enableRowSelection={false}
          withNavigation={true}
        />
      </SurfacePanel>
    );
  }

  /* ******************************** RENDERING ******************************* */

  return (
    <Stack className={classes.container} {...props}>
      <Group
        className={classes.toolbar}
        justify={scope === 'my' ? 'space-between' : 'end'}
      >
        {scope === 'my' ? (
          <SegmentedControl
            styles={{
              root: { backgroundColor: palette.blackroomLightGray1 },
            }}
            color="blackroomBlue"
            value={navigationView}
            data={['index', 'folder'].map((view) => ({
              value: view,
              label: (
                <Group gap="0.5rem" wrap="nowrap">
                  <ThemeIcon
                    variant="transparent"
                    color={
                      view === navigationView
                        ? 'blackroomWhite'
                        : palette.blackroomDarkGray4
                    }
                    size="xs"
                  >
                    {view === 'index' ? (
                      <IconListNumbers />
                    ) : (
                      <IconFolderOpen />
                    )}
                  </ThemeIcon>
                  <Text fz="sm" fw={FONT_WEIGHT.normal}>
                    {t(`Resource:ResourcePane.navigation.${view}.label`)}
                  </Text>
                </Group>
              ),
            }))}
            onChange={(v) => {
              setNavigationView(v as ResourceNavigationView);
            }}
          />
        ) : null}
        <ActionBar
          selectedResources={selectedResources}
          scope={scope}
          selectAll={selectAll}
          table={table}
          setColumnVisibility={setResourceTableColumnVisibility}
          columnVisibility={resourceTableColumnVisibility}
          requestVariables={requestVariables}
        />
      </Group>
      <Group
        gap="0"
        align="start"
        wrap="nowrap"
        justify="space-between"
        style={{ overflow: 'auto' }}
      >
        {getIndexedFolders()}
        {getResources()}
      </Group>
    </Stack>
  );
}

export default ResourcePane;
